<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Cash Book
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <!-- Date Filters -->
                <v-row class="py-2 pl-2 align-center">
                  <v-col lg="3" md="4" cols="12">
                    <v-date-input 
                      input-format="dd/mm/yyyy"
                      v-model="filters.from_date" 
                      label="From Date" 
                      density="compact" 
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-start" 
                      variant="outlined" 
                      bg-color="grey-lighten-4" 
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col lg="3" md="4" cols="12">
                    <v-date-input 
                      input-format="dd/mm/yyyy"
                      v-model="filters.to_date" 
                      label="To Date" 
                      density="compact" 
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-end" 
                      variant="outlined" 
                      bg-color="grey-lighten-4" 
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col lg="3" md="4" cols="12">
                    <v-autocomplete
                      v-model="filters.acc_account_head_id"
                      :items="paymentAccountOptions"
                      item-title="text"
                      item-value="value"
                      label="All Account Head"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      prepend-inner-icon="mdi-bank"
                      clearable
                      :loading="loadingAccountHeads"
                    ></v-autocomplete>
                  </v-col>
                  <v-col lg="3" md="12" cols="12">
                    <div class="d-flex justify-start align-center flex-wrap">
                      <v-btn class="bg-primary mb-4" type="submit" @click="loadCashBook" elevation="2" >
                        <v-icon left>mdi-magnify</v-icon> Search
                      </v-btn>
                    </div>
                  </v-col>
                
                </v-row>

                <!-- Export Buttons -->
                <v-row v-if="cashBookData && cashBookData.length > 0" class="py-2 mt-2">
                  <v-col cols="12">
                    <div class="d-flex justify-center">
                      <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success" @click="exportToExcel" :disabled="!cashBookData">EXCEL</v-btn>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red" @click="exportToPDF" :disabled="!cashBookData">PDF</v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>
                </v-row>

                <!-- Cash Book Report -->
                <v-card v-if="cashBookData && cashBookData.length > 0" class="mt-2">
                  <v-card-title class="text-center">
                    <div class="w-100">
                      <div class="text-h5 font-weight-bold">Cash Book for: {{ formattedDate(filters.from_date) }} to {{ formattedDate(filters.to_date) }}</div>
                    </div>
                  </v-card-title>

                  <v-card-text class="pa-0">
                    <!-- Cash Book Report - Individual Account Sections -->
                    <div class="cash-book-report">
                      <div v-for="(account, index) in cashBookData" :key="account.id" class="cash-book-account-section">
                        <!-- Account Header -->
                        <div class="account-header">
                          <strong>A/C Name: {{ account.account_group.name }}: {{ account.name }} - ({{ account.code }})</strong>
                        </div>

                        <!-- Cash Book Table -->
                        <div class="cash-book-table-exact">
                          <!-- Table Header -->
                          <div class="cash-book-header">
                            <div class="header-date">Date</div>
                            <div class="header-particular">Particulars</div>
                            <div class="header-voucher">Voucher No</div>
                            <div class="header-party">Party</div>
                            <div class="header-cheque">Cheque No</div>
                            <div class="header-breakdown">Break Down</div>
                            <div class="header-payment">Payment</div>
                            <div class="header-receipt">Receipt</div>
                            <div class="header-balance">Balance</div>
                          </div>

                          <!-- Opening Balance Row -->
                          <div class="cash-book-row opening-balance">
                            <div class="cash-book-date">{{ formattedDate(filters.from_date) }}</div>
                            <div class="cash-book-particular">Opening Balance</div>
                            <div class="cash-book-voucher">-</div>
                            <div class="cash-book-party">-</div>
                            <div class="cash-book-cheque">-</div>
                            <div class="cash-book-breakdown">-</div>
                            <div class="cash-book-payment">-</div>
                            <div class="cash-book-receipt">-</div>
                            <div class="cash-book-balance">{{ formatBalance(account.opening_balance) }}</div>
                          </div>

                          <!-- Transaction Rows -->
                          <div v-for="transaction in account.transactions" :key="transaction.id" class="cash-book-row">
                            <div class="cash-book-date">{{ formattedDate(transaction.voucher_date) }}</div>
                            <div class="cash-book-particular">{{ transaction.remarks }}</div>
                            <div class="cash-book-voucher">{{ transaction.code }}</div>
                            <div class="cash-book-party"></div>
                            <div class="cash-book-cheque">{{ getChequeNumber(transaction) }}</div>
                            <div class="cash-book-breakdown">
                              <div v-if="transaction.break_down && transaction.break_down.length > 0" class="breakdown-details">
                                <div v-for="bd in transaction.break_down" :key="bd.acc_account_head_id" class="breakdown-item">
                                  <span class="breakdown-name">{{ bd.account_head ? bd.account_head.name : 'N/A' }}</span>
                                  <span class="breakdown-amount">{{ formatCurrency(bd.amount) }}</span>
                                </div>
                                <div class="breakdown-subtotal">Sub Total</div>
                                <div class="breakdown-total">{{ formatCurrency(transaction.receipt_amount || transaction.payment_amount) }}</div>
                              </div>
                              <div v-else>-</div>
                            </div>
                            <div class="cash-book-payment">{{ formatCurrency(transaction.payment_amount) }}</div>
                            <div class="cash-book-receipt">{{ formatCurrency(transaction.receipt_amount) }}</div>
                            <div class="cash-book-balance">{{ formatBalance(transaction.balance) }}</div>
                          </div>

                          <!-- Account Total Row -->
                          <div class="cash-book-row account-total">
                            <div class="cash-book-date"></div>
                            <div class="cash-book-particular"><strong>Total</strong></div>
                            <div class="cash-book-voucher"></div>
                            <div class="cash-book-party"></div>
                            <div class="cash-book-cheque"></div>
                            <div class="cash-book-breakdown"></div>
                            <div class="cash-book-payment"><strong>{{ formatCurrency(account.total_payment) }}</strong></div>
                            <div class="cash-book-receipt"><strong>{{ formatCurrency(account.total_receipt) }}</strong></div>
                            <div class="cash-book-balance"><strong>{{ formatBalance(account.closing_balance) }}</strong></div>
                          </div>

                          <!-- Closing Balance Row -->
                          <div class="cash-book-row closing-balance">
                            <div class="cash-book-date">{{ formattedDate(filters.to_date) }}</div>
                            <div class="cash-book-particular"><strong>Closing Balance</strong></div>
                            <div class="cash-book-voucher">-</div>
                            <div class="cash-book-party">-</div>
                            <div class="cash-book-cheque">-</div>
                            <div class="cash-book-breakdown">-</div>
                            <div class="cash-book-payment">-</div>
                            <div class="cash-book-receipt">-</div>
                            <div class="cash-book-balance"><strong>{{ formatBalance(account.closing_balance) }}</strong></div>
                          </div>
                        </div>

                        <!-- Spacing between accounts -->
                        <div v-if="index < cashBookData.length - 1" class="account-spacing"></div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- No Data Message -->
                <v-card v-else-if="!loading && (!cashBookData || cashBookData.length === 0)" class="mt-2">
                  <v-card-text class="text-center py-8">
                    <v-icon size="64" color="grey">mdi-cash-register</v-icon>
                    <div class="text-h6 mt-2">No Cash Book Data Found</div>
                    <div class="text-body-2 text-grey">Please adjust your filters and try again</div>
                  </v-card-text>
                </v-card>

                <!-- Loading State -->
                <v-card v-if="loading" class="mt-2">
                  <v-card-text class="text-center py-8">
                    <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                    <div class="text-h6 mt-2">Loading Cash Book Report...</div>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import formattedDate from '@/plugins/dateFormatter';
import { useStore } from 'vuex'

const store = useStore()
import { mapGetters } from 'vuex'

export default {
  name: 'CashBookReport',
  data() {
    return {
      panel: [0],
      loading: false,
      loadingAccountHeads: false,
      cashBookData: null,
      paymentAccountOptions: [],
      filters: {
        from_date: new Date().toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0],
        acc_account_head_id: null
      }
    }
  },
  computed: {
    ...mapGetters(['getBranchId'])
  },
  mounted() {
    this.loadPaymentAccountHeads()
  },
  methods: {
    formattedDate,
    async loadPaymentAccountHeads() {
      this.loadingAccountHeads = true
      try {
        const response = await this.$store.dispatch('getPaymentAccountHeads', { 
          payment_method: 'CASH' 
        })
        this.paymentAccountOptions = response.data.map(item => ({
          text: `${item.code} - ${item.name}`,
          value: item.id
        }))
      } catch (error) {
        console.error('Error loading payment account heads:', error)
        this.$toast.error('Failed to load payment account heads')
      } finally {
        this.loadingAccountHeads = false
      }
    },

    async loadCashBook() {
      this.loading = true
      try {
        const params = {
          from_date: formattedDate(this.filters.from_date),
          to_date: formattedDate(this.filters.to_date),
          acc_account_head_id: this.filters.acc_account_head_id
        }
        
        const response = await this.$store.dispatch('getCashBook', params)
        if (response && response.success) {
          this.cashBookData = response.data
          this.$toast.success('Cash book report generated successfully')
        } else {
          this.$toast.error(response?.message || 'Failed to generate cash book report')
        }
      } catch (error) {
        console.error('Error loading cash book:', error)
        const errorMessage = error?.message || error?.response?.data?.message || 'Failed to load cash book report'
        this.$toast.error(errorMessage)
        this.cashBookData = null
      } finally {
        this.loading = false
      }
    },

    resetFilters() {
      this.filters = {
        from_date: new Date().toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0],
        acc_account_head_id: null
      }
      this.cashBookData = null
    },

    formatCurrency(amount) {
      if (!amount || amount === 0) return '0.00'
      return parseFloat(amount).toFixed(2)
    },

    formatBalance(balance) {
      if (!balance) return '0.00'
      
      // Handle wrapped balance object with nature (future compatibility)
      if (typeof balance === 'object' && balance.balance !== undefined) {
        const amt = parseFloat(balance.balance).toFixed(2)
        const nature = balance.nature || ''
        return nature ? `${amt} ${nature}` : amt
      }
      
      // Handle string format from API - e.g., "15000.00" or "(2000.00)" for negative
      if (typeof balance === 'string') {
        // If already formatted with parentheses for negative, return as-is
        if (balance.includes('(') && balance.includes(')')) {
          return balance
        }
        // Otherwise parse and format
        const num = parseFloat(balance.replace(/[(),]/g, ''))
        return isNaN(num) ? '0.00' : num.toFixed(2)
      }
      
      // Handle simple number
      return parseFloat(balance).toFixed(2)
    },

    getPartyName(transaction) {
      // Try to get party name from break down accounts
      if (transaction.break_down && transaction.break_down.length > 0) {
        const partyAccount = transaction.break_down.find(bd => 
          bd.account_head && 
          (bd.account_head.name.toLowerCase().includes('supplier') || 
           bd.account_head.name.toLowerCase().includes('customer') ||
           bd.account_head.name.toLowerCase().includes('party'))
        )
        return partyAccount ? partyAccount.account_head.name : 'Local supplier'
      }
      return 'Local supplier'
    },

    getChequeNumber(transaction) {
      // This would need to be provided by the backend API
      // For now, return empty string
      return ''
    },

    exportToExcel() {
      if (!this.cashBookData || this.cashBookData.length === 0) return

      const data = [
        ['Cash Book'],
        [`Date: ${this.formattedDate(this.filters.from_date)} to ${this.formattedDate(this.filters.to_date)}`],
        []
      ]

      // Loop through each account
      this.cashBookData.forEach((account) => {
        // Account Header
        data.push([`A/C Name: ${account.account_group.name}: ${account.name} - (${account.code})`])
        data.push([])
        
        // Table Header
        data.push(['Date', 'Particulars', 'Voucher No', 'Party', 'Cheque No', 'Break Down', 'Payment', 'Receipt', 'Balance'])
        
        // Opening Balance
        data.push([
          this.formattedDate(this.filters.from_date),
          'Opening Balance',
          '-',
          '-',
          '-',
          '-',
          '-',
          '-',
          this.formatBalance(account.opening_balance)
        ])

        // Transactions
        account.transactions.forEach((transaction) => {
          const particular = transaction.remarks || 'Transaction'
          const cheque = this.getChequeNumber(transaction)
          const breakdown = transaction.break_down && transaction.break_down.length > 0 
            ? transaction.break_down.map(bd => `${bd.account_head ? bd.account_head.name : 'N/A'}: ${this.formatCurrency(bd.amount)}`).join('\n') + '\nSub Total\n' + this.formatCurrency(transaction.receipt_amount || transaction.payment_amount)
            : '-'
          
          data.push([
            this.formattedDate(transaction.voucher_date),
            particular,
            transaction.code,
            '',
            cheque,
            breakdown,
            this.formatCurrency(transaction.payment_amount),
            this.formatCurrency(transaction.receipt_amount),
            this.formatBalance(transaction.balance)
          ])
        })

        // Total
        data.push([
          '',
          'Total',
          '',
          '',
          '',
          '',
          this.formatCurrency(account.total_payment),
          this.formatCurrency(account.total_receipt),
          this.formatBalance(account.closing_balance)
        ])

        // Closing Balance
        data.push([
          this.formattedDate(this.filters.to_date),
          'Closing Balance',
          '-',
          '-',
          '-',
          '-',
          '-',
          '-',
          this.formatBalance(account.closing_balance)
        ])

        data.push([])
        data.push([])
      })

      this.$exportTableToExcel(data, 'cash_book_' + this.$fileDate())
    },

    exportToPDF() {
      if (!this.cashBookData || this.cashBookData.length === 0) return

      const columns = ['Date', 'Particulars', 'Voucher No', 'Party', 'Cheque No', 'Break Down', 'Payment', 'Receipt', 'Balance']
      const rows = []

      // Loop through each account
      this.cashBookData.forEach((account, accountIndex) => {
        // Account Header (as a special row spanning all columns)
        rows.push([{
          content: `A/C Name: ${account.account_group.name}: ${account.name} - (${account.code})`,
          colSpan: 9,
          styles: { 
            fillColor: [245, 245, 245], 
            fontStyle: 'bold',
            halign: 'center'
          }
        }])
        
        // Opening Balance
        rows.push([
          this.formattedDate(this.filters.from_date),
          'Opening Balance',
          '-',
          '-',
          '-',
          '-',
          '-',
          '-',
          this.formatBalance(account.opening_balance)
        ])

        // Transactions
        account.transactions.forEach((transaction) => {
          const particular = transaction.remarks || 'Transaction'
          const cheque = this.getChequeNumber(transaction)
          const breakdown = transaction.break_down && transaction.break_down.length > 0 
            ? transaction.break_down.map(bd => `${bd.account_head ? bd.account_head.name : 'N/A'}: ${this.formatCurrency(bd.amount)}`).join('\n') + '\nSub Total\n' + this.formatCurrency(transaction.receipt_amount || transaction.payment_amount)
            : '-'
          
          rows.push([
            this.formattedDate(transaction.voucher_date),
            particular,
            transaction.code,
            '',
            cheque,
            breakdown,
            this.formatCurrency(transaction.payment_amount),
            this.formatCurrency(transaction.receipt_amount),
            this.formatBalance(transaction.balance)
          ])
        })

        // Total
        rows.push([
          '',
          'Total',
          '',
          '',
          '',
          '',
          this.formatCurrency(account.total_payment),
          this.formatCurrency(account.total_receipt),
          this.formatBalance(account.closing_balance)
        ])

        // Closing Balance
        rows.push([
          this.formattedDate(this.filters.to_date),
          'Closing Balance',
          '-',
          '-',
          '-',
          '-',
          '-',
          '-',
          this.formatBalance(account.closing_balance)
        ])

        // Add spacing between accounts (except for the last one)
        if (accountIndex < this.cashBookData.length - 1) {
          rows.push(['', '', '', '', '', ''])
        }
      })

      this.$exportTableToPDF(
        columns,
        rows,
        `Cash Book - ${this.formattedDate(this.filters.from_date)} to ${this.formattedDate(this.filters.to_date)}`,
        'cash_book_' + this.$fileDate(),
        'landscape',
        {
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fontSize: 9, fillColor: [66, 139, 202], fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 20, halign: 'center' },  // Date
            1: { cellWidth: 50, halign: 'left' },    // Particulars
            2: { cellWidth: 20, halign: 'center' },  // Voucher No
            3: { cellWidth: 25, halign: 'center' },  // Party
            4: { cellWidth: 20, halign: 'center' },  // Cheque No
            5: { cellWidth: 30, halign: 'left' },    // Break Down
            6: { cellWidth: 20, halign: 'right' },   // Payment
            7: { cellWidth: 20, halign: 'right' },   // Receipt
            8: { cellWidth: 25, halign: 'right' }    // Balance
          },
          didParseCell: function(data) {
            // Bold style for Total and Closing Balance rows
            if (data.cell.text[0] === 'Total' || data.cell.text[0] === 'Closing Balance') {
              data.cell.styles.fontStyle = 'bold'
              data.cell.styles.fillColor = [240, 240, 240]
            }
          }
        }
      )
    }
  }
}
</script>

<style scoped>
.panel-header {
  font-weight: bold;
  font-size: 1.1rem;
}

/* Cash Book Table Style */
.cash-book-account-section {
  margin-bottom: 20px;
}

.account-header {
  padding: 2px 4px;
  border-bottom: 1px solid #000;
  font-weight: bold;
  text-align: center;
  font-size: 12px;
}

.cash-book-table-exact {
  font-family: 'Roboto', sans-serif;
  width: 100%;
  background-color: white;
  border: 1px solid #000;
  overflow: hidden;
  margin-bottom: 20px;
  table-layout: fixed;
}

/* Header Styles */
.cash-book-header {
  display: flex;
  align-items: stretch;
  font-weight: bold;
  border-bottom: 1px solid #000;
}

.header-date {
  width: 90px;
  min-width: 90px;
  max-width: 90px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-particular {
  width: 180px;
  min-width: 180px;
  max-width: 180px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: left;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
}

.header-voucher {
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-party {
  width: 100px;
  min-width: 100px;
  max-width: 100px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-cheque {
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-breakdown {
  width: 130px;
  min-width: 130px;
  max-width: 130px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-payment {
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-receipt {
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-balance {
  flex: 1;
  padding: 2px 4px;
  font-size: 12px;
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

/* Row Styles */
.cash-book-row {
  display: flex;
  align-items: stretch;
  min-height: 32px;
  border-bottom: 1px solid #000;
}

.cash-book-date {
  width: 90px;
  min-width: 90px;
  max-width: 90px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cash-book-particular {
  width: 180px;
  min-width: 180px;
  max-width: 180px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: left;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
}

.cash-book-voucher {
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cash-book-party {
  width: 100px;
  min-width: 100px;
  max-width: 100px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cash-book-cheque {
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cash-book-breakdown {
  width: 130px;
  min-width: 130px;
  max-width: 130px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: left;
  border-right: 1px solid #000;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100%;
}

.cash-book-payment {
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: right;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.cash-book-receipt {
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  padding: 2px 4px;
  font-size: 12px;
  text-align: right;
  border-right: 1px solid #000;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.cash-book-balance {
  flex: 1;
  padding: 2px 4px;
  font-size: 12px;
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 8px;
}

.opening-balance {
  font-style: italic;
}

.closing-balance {
  font-style: italic;
  font-weight: bold;
}

.account-total {
  border-top: 1px solid #000;
  font-weight: bold;
}

.breakdown-details {
  font-size: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
}

.breakdown-item {
  padding: 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
  min-height: 12px;
}

.breakdown-name {
  font-weight: 400;
  flex: 1;
  text-align: left;
}

.breakdown-amount {
  font-weight: 500;
  text-align: right;
}

.breakdown-subtotal {
  padding: 1px 0;
  font-weight: 500;
  text-align: right;
  border-top: 1px solid #000;
  margin-top: 1px;
  min-height: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.breakdown-total {
  padding: 1px 0;
  font-weight: bold;
  text-align: right;
  border-top: 1px solid #000;
  margin-top: 1px;
  min-height: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.account-spacing {
  height: 20px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .cash-book-date,
  .cash-book-voucher,
  .cash-book-particular,
  .cash-book-receipt,
  .cash-book-payment,
  .cash-book-balance {
    font-size: 10px;
    padding: 2px 4px;
  }
  
  .header-date,
  .header-voucher,
  .header-particular,
  .header-receipt,
  .header-payment,
  .header-balance {
    font-size: 10px;
    padding: 2px 4px;
    
  }
}
</style>

